import React from 'react';
import getExtensionPropertyValueByKey, { CommercePropertyValueEnum } from '../../../../utils/getExtensionPropertyValueByKey';
import { SupersessionKey } from '../../models/SupersessionKey';
import { CommerceProperty } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { convertStringToArray, getIsAlternateWarehouse } from '../../../../utils/alternateWarehouseHelper';

export interface IWarehouseMapping {
    warehouseId: string;
    warehouseName: string;
}

interface IInventoryBreakdownComponentProps {
    extensionProperties: CommerceProperty[] | undefined;
    itemId: string;
    canBackorder: boolean;
    canChangeWarehouse?: boolean;
    onWarehouseChange?: (whId: string) => void;
    allowableText: string;
    alternateText: string;
    designatedDealerTypes: string;
    productCodeWhitelist: string;
    productQuantity?: number | undefined;
    setShouldShowQtyMessage?: React.Dispatch<React.SetStateAction<boolean>>;
    warehouseMappings: IWarehouseMapping[];
    setIsAlternateWarehouse?: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedWarehouseId?: React.Dispatch<React.SetStateAction<string | null>>;
    setHasClickedAlternateWarehouse?: React.Dispatch<React.SetStateAction<boolean>>;
    currentWarehouse?: string | null;
}

export type WarehouseInventoryType = {
    WarehouseId: string;
    IsAllowable: boolean;
    Available: number;
    Name: string;
};

export const InventoryBreakdownComponent: React.FC<IInventoryBreakdownComponentProps> = props => {
    const {
        extensionProperties,
        canChangeWarehouse,
        onWarehouseChange,
        productQuantity,
        warehouseMappings,
        setIsAlternateWarehouse,
        setHasClickedAlternateWarehouse,
        setSelectedWarehouseId,
        currentWarehouse,
        designatedDealerTypes,
        productCodeWhitelist
    } = props;
    const [warehouseData, setWarehouseData] = React.useState<WarehouseInventoryType[]>([]);
    const [hasError, setHasError] = React.useState<boolean>(false);

    const designatedDealerTypesArr = convertStringToArray(designatedDealerTypes);
    const productCodeWhitelistArr = convertStringToArray(productCodeWhitelist);

    const handleSelectWarehouseClick = (e: React.MouseEvent, warehouse: WarehouseInventoryType) => {
        e.preventDefault();
        if (onWarehouseChange && setIsAlternateWarehouse) {
            const isPrimaryWarehouse = warehouseData[0].WarehouseId === warehouse.WarehouseId;
            const isAlternateWarehouse = isPrimaryWarehouse
                ? !warehouse.IsAllowable
                : getIsAlternateWarehouse({
                      designatedDealerTypesArr,
                      productCodeWhitelistArr,
                      extensionProperties
                  });
            setIsAlternateWarehouse(isAlternateWarehouse);
            if (isAlternateWarehouse && setHasClickedAlternateWarehouse && setSelectedWarehouseId) {
                setHasClickedAlternateWarehouse(true);
                setSelectedWarehouseId(warehouse.WarehouseId);
            } else {
                onWarehouseChange(warehouse.WarehouseId);
            }
        }
    };

    const shouldDisableWarehouseSelect = (warehouse: WarehouseInventoryType) => {
        if (ArrayExtensions.hasElements(warehouseData) && warehouse.WarehouseId === warehouseData[0].WarehouseId) {
            return false;
        }
        const anyWarehouseHasSufficientQty = warehouseData.some(wh => wh.Available >= (productQuantity ?? 0));
        props.setShouldShowQtyMessage?.(!anyWarehouseHasSufficientQty);
        return warehouse.Available <= 0 || warehouse.Available < productQuantity!;
    };

    const renderWarehouseSelectLink = (warehouse: WarehouseInventoryType) => {
        if (shouldDisableWarehouseSelect(warehouse)) {
            return <p className={'gdn-inventory-link__disable'}>Unavailable</p>;
        }

        const selectedWarehouse = currentWarehouse && currentWarehouse.trim() !== '' ? currentWarehouse : warehouseData[0]?.WarehouseId;

        if (warehouse.WarehouseId === selectedWarehouse) {
            return <p className={'gdn-inventory-selected'}>Selected Warehouse</p>;
        }

        return (
            <a href={'javascript:void(0)'} className={'gdn-inventory-link'} onClick={e => handleSelectWarehouseClick(e, warehouse)}>
                Select Warehouse
            </a>
        );
    };

    React.useEffect(() => {
        try {
            const warehouseJsonData = getExtensionPropertyValueByKey(
                extensionProperties,
                SupersessionKey.InventoryBreakdown,
                CommercePropertyValueEnum.string
            ) as string;
            const data = JSON.parse(warehouseJsonData) as WarehouseInventoryType[];
            setWarehouseData(data || []);
            setHasError(false);
        } catch {
            setHasError(true);
        }
    }, [extensionProperties]);

    const allowableText = props.allowableText || 'Allowable';
    const alternateText = props.alternateText || '-------------------';

    const getWarehouseFriendlyName = (warehouseId: string) => {
        return warehouseMappings?.find(mapping => mapping.warehouseId === warehouseId)?.warehouseName;
    };

    const formatWarehouseName = (name: string) => {
        return name.replace(/gardner\s*/i, '');
    };

    return (
        <div className='inventory-details'>
            {hasError ? <p>Inventory unavailable, please try again later</p> : null}
            {warehouseData.length ? (
                <table>
                    <thead>
                        <tr>
                            <th>Warehouse</th>
                            <th>Available</th>
                            <th></th>
                            {canChangeWarehouse ? <th /> : undefined}
                        </tr>
                    </thead>
                    <tbody>
                        {warehouseData.map((wh, idx) => (
                            <tr key={wh.WarehouseId}>
                                <td>
                                    {getWarehouseFriendlyName(wh.WarehouseId) ?? formatWarehouseName(wh.Name)} - {wh.WarehouseId}
                                </td>
                                <td>{wh.Available}</td>
                                <td>
                                    {idx === 0 && wh.IsAllowable
                                        ? allowableText
                                        : getIsAlternateWarehouse({
                                              designatedDealerTypesArr,
                                              productCodeWhitelistArr,
                                              extensionProperties
                                          })
                                        ? alternateText
                                        : allowableText}
                                </td>
                                {canChangeWarehouse ? <td>{renderWarehouseSelectLink(wh)}</td> : undefined}
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div>
                    <p>No Inventory Data Available</p>
                </div>
            )}
        </div>
    );
};
